import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types (espelham backend procurement.dto.ts / purchase-requests.controller.ts) ── */

export type PurchaseRequestStatus =
  | 'DRAFT'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED'
  | 'CONVERTED'
  | 'CANCELLED';

export interface PurchaseRequestItem {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  quantity: number;
  unitOfMeasure: string;
  estimatedUnitCost: number;
  estimatedTotalCost: number;
  requiredDate: string;
  justification: string;
  status: string;
  rejectionReason: string | null;
}

export interface PurchaseRequest {
  id: string;
  tenantId: string;
  companyId: string;
  branchId: string;
  requestNumber: string;
  status: PurchaseRequestStatus;
  items: PurchaseRequestItem[];
  requestedById: string;
  requestedBy: string;
  departmentId: string | null;
  justification: string;
  urgent: boolean;
  approvedById: string | null;
  approvedBy: string | null;
  rejectedById: string | null;
  rejectionReason: string | null;
  convertedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** GET /procurement/purchase-requests → { items, total } (sem page/limit no envelope). */
export interface PurchaseRequestsListResponse {
  items: PurchaseRequest[];
  total: number;
}

export interface CreatePurchaseRequestItemInput {
  productId: string;
  productName?: string;
  quantity: number;
  unitOfMeasure?: string;
  estimatedUnitCost: number;
  requiredDate: string;
  justification: string;
}

/** POST /procurement/purchase-requests — companyId/branchId/requestedBy vêm da sessão. */
export interface CreatePurchaseRequestInput {
  departmentId?: string;
  justification: string;
  urgent?: boolean;
  items: CreatePurchaseRequestItemInput[];
}

export interface PurchaseRequestsParams {
  status?: string;
  page?: number;
  limit?: number;
}

const BASE_PATH = '/procurement/purchase-requests';

/* ── Queries ── */

export function usePurchaseRequests(tenantId: string, params?: PurchaseRequestsParams) {
  const status = params?.status;
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [...queryKeys.procurement.requests(tenantId), { status: status ?? 'ALL', page, limit }],
    queryFn: () =>
      apiClient.get<PurchaseRequestsListResponse>(BASE_PATH, {
        params: {
          page: String(page),
          limit: String(limit),
          ...(status && status !== 'ALL' ? { status } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

export function usePurchaseRequest(tenantId: string, id: string) {
  return useQuery({
    queryKey: [...queryKeys.procurement.requests(tenantId), id],
    queryFn: () => apiClient.get<PurchaseRequest>(`${BASE_PATH}/${id}`),
    enabled: !!tenantId && !!id,
  });
}

/* ── Mutations (transições confirmadas em purchase-requests.controller.ts) ── */

function useInvalidatePurchaseRequests(tenantId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.procurement.requests(tenantId) });
  };
}

export function useCreatePurchaseRequest(tenantId: string) {
  const invalidate = useInvalidatePurchaseRequests(tenantId);
  return useMutation({
    mutationFn: (input: CreatePurchaseRequestInput) =>
      apiClient.post<PurchaseRequest>(BASE_PATH, input),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** POST :id/submit — sem body (DRAFT → PENDING_APPROVAL). */
export function useSubmitPurchaseRequest(tenantId: string) {
  const invalidate = useInvalidatePurchaseRequests(tenantId);
  return useMutation({
    mutationFn: (id: string) => apiClient.post<PurchaseRequest>(`${BASE_PATH}/${id}/submit`),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** POST :id/approve — sem body (PENDING_APPROVAL → APPROVED). */
export function useApprovePurchaseRequest(tenantId: string) {
  const invalidate = useInvalidatePurchaseRequests(tenantId);
  return useMutation({
    mutationFn: (id: string) => apiClient.post<PurchaseRequest>(`${BASE_PATH}/${id}/approve`),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** POST :id/reject — body { reason } (→ REJECTED). */
export function useRejectPurchaseRequest(tenantId: string) {
  const invalidate = useInvalidatePurchaseRequests(tenantId);
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post<PurchaseRequest>(`${BASE_PATH}/${id}/reject`, { reason }),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** POST :id/cancel — sem body (DRAFT|PENDING_APPROVAL → CANCELLED). */
export function useCancelPurchaseRequest(tenantId: string) {
  const invalidate = useInvalidatePurchaseRequests(tenantId);
  return useMutation({
    mutationFn: (id: string) => apiClient.post<PurchaseRequest>(`${BASE_PATH}/${id}/cancel`),
    onSuccess: () => {
      invalidate();
    },
  });
}
